import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const TagsInput = ({
  handleQueryInput = () => {},
  isEmpty = false,
  preEnteredTags = [],
  unsetPreRenderedTag = () => {},
  storeInput = () => {}
}) => {
  const [input, setInput] = useState('');
  const [tags, setTags] = useState([]);
  const [isKeyReleased, setIsKeyReleased] = useState(false);

  const { t } = useTranslation();

  const onChange = (e) => {
    const { value } = e.target;
    setInput(value);
    storeInput(value);
  };

  // handle key presses
  const onKeyDown = (e) => {
    const trimmedInput = input.trim();

    if (
      (e.key === ',' || e.key === 'Enter') &&
      trimmedInput &&
      !tags.includes(trimmedInput)
    ) {
      e.preventDefault();

      const updatedTags = [...tags, trimmedInput];

      setTags(updatedTags);
      handleQueryInput(updatedTags);
      setInput('');
      return;
    }

    if (e.key === 'Backspace' && !input && tags.length && isKeyReleased) {
      e.preventDefault();

      const updatedTags = tags.slice(0, -1);

      setTags(updatedTags);
      handleQueryInput(updatedTags);
    }

    setIsKeyReleased(false);
  };

  // handle keyup
  const onKeyUp = () => {
    setIsKeyReleased(true);
  };

  // delete tag
  const deleteTag = (index) => {
    const updatedTags = tags.filter((_, i) => {
      return i !== index;
    });

    setTags(updatedTags);
    handleQueryInput(updatedTags);

    if (preEnteredTags && tags[index] === preEnteredTags) {
      unsetPreRenderedTag();
    }
  };

  useEffect(() => {
    if (!preEnteredTags) return;

    const initialTags = Array.isArray(preEnteredTags)
      ? preEnteredTags
      : [preEnteredTags];

    setTags((current) => {
      return current.length ? current : initialTags;
    });
  }, [preEnteredTags]);

  useEffect(() => {
    if (isEmpty && tags.length > 0) {
      setTags([]);
      handleQueryInput([]);
    }
  }, [isEmpty]);

  return (
    <div className="flex flex-wrap items-center gap-2 w-full py-1 px-5 border border-slate-200 rounded-md">
      {tags.map((tag, index) => {
        return (
          <div
            className="inline-flex px-3 py-1 border border-slate-200 items-center gap-x-2 font-semibold rounded-sm"
            // eslint-disable-next-line react/no-array-index-key
            key={index}
          >
            <span className="text-sm">{tag}</span>
            <button
              type="button"
              className="text-xs"
              onClick={() => {
                return deleteTag(index);
              }}
            >
              x
            </button>
          </div>
        );
      })}
      <input
        className={`p-2 bg-transparent text-base text-gray-500 ring-0 focus:outline-hidden focus:ring-0 ${
          tags.length > 0 ? 'w-10/12' : 'w-full'
        }`}
        type="text"
        value={input}
        placeholder={t('findInvestors.whatAreYouLookingFor')}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onChange={onChange}
      />
    </div>
  );
};

export default TagsInput;

TagsInput.propTypes = {
  handleQueryInput: PropTypes.func,
  isEmpty: PropTypes.bool,
  preEnteredTags: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  unsetPreRenderedTag: PropTypes.func,
  storeInput: PropTypes.func
};
